import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExtensionIcon from '@mui/icons-material/Extension';
import SaveIcon from '@mui/icons-material/Save';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LockIcon from '@mui/icons-material/Lock';
import SuperAdminService from '../../services/SuperAdminService';
import { MODULE_DEFINITIONS, ALL_MODULE_IDS, DEFAULT_ON_MODULE_IDS, OPT_IN_MODULE_IDS, type ModuleId } from '../../config/moduleAccess';
import { AppDatePicker } from '../common/AppDatePicker';

interface ModuleSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  company: { id: string; name: string } | null;
}

/** ISO/Date değerini `<input type="date">` formatına (YYYY-MM-DD, yerel) çevirir. */
const toDateInputValue = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** YYYY-MM-DD değerini o günün SONU (yerel 23:59) olarak ISO'ya çevirir — gün boyu erişim kalsın. */
const dateInputToIso = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(`${value}T23:59:00`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * SuperAdmin/bayi — şirket bazlı modül aç/kapa + demo süresi diyaloğu.
 *
 * İKİ MOD:
 *  - MERKEZ (superadmin): açık modülleri ve demo süresini yönetir. Şirket bir bayiye
 *    bağlıysa ayrıca "bayiye izin verilen modüller" TAVANINI belirler.
 *  - BAYİ (scopeIsDealer): yalnızca merkezin izin verdiği modülleri aktive edebilir.
 *    "Tümü açık" anahtarı ve demo bölümü görünmez; tavan dışı modüller kilitlidir.
 *
 * Veri modeli: modules (açık modüller) her zaman dealerAllowedModules (tavan) kümesinin
 * ALT KÜMESİ olmak zorundadır; kural sunucuda da zorlanır (403).
 */
const ModuleSettingsDialog: React.FC<ModuleSettingsDialogProps> = ({ open, onClose, company }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** null = kısıtlama yok (tüm modüller açık) */
  const [modules, setModules] = useState<string[] | null>(null);
  /** Bayiye izin verilen tavan; null = bayi kısıtlaması yok, [] = hiç izin yok. */
  const [dealerAllowed, setDealerAllowed] = useState<string[] | null>(null);
  /** ISO tarih; null = demo değil (süresiz). */
  const [demoUntil, setDemoUntil] = useState<string | null>(null);
  /** Diyaloğu açan kullanıcı bayi mi (sunucudan gelir, istemcide varsayılmaz). */
  const [scopeIsDealer, setScopeIsDealer] = useState(false);
  /** Şirket bir bayiye atanmış mı — tavan bölümü yalnız bu durumda gösterilir. */
  const [isDealerManaged, setIsDealerManaged] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** Tavan değişti mi — kaydetmede ek istek gerektirir. */
  const [allowedDirty, setAllowedDirty] = useState(false);

  useEffect(() => {
    if (!open || !company) return;
    setError(null);
    setSuccess(null);
    setDirty(false);
    setAllowedDirty(false);
    setLoading(true);
    SuperAdminService.getInstance()
      .getCompanyModules(company.id)
      .then((info) => {
        // Bayi "tümü açık" (null) gönderemez; eski kayıtlarda null gelirse
        // listeyi somutlaştırırız ki kaydetme isteği geçerli olsun.
        setModules(info.scopeIsDealer ? info.modules ?? [...DEFAULT_ON_MODULE_IDS] : info.modules);
        setDealerAllowed(info.dealerAllowedModules);
        setDemoUntil(info.demoUntil);
        setScopeIsDealer(info.scopeIsDealer);
        setIsDealerManaged(info.isDealerManaged);
      })
      .catch((e) => setError(e?.message || 'Modül bilgisi alınamadı'))
      .finally(() => setLoading(false));
  }, [open, company]);

  if (!company) return null;

  const allEnabled = modules === null;
  /** Bayi kısıtlaması yok mu (tavan tanımsız). */
  const noDealerLimit = dealerAllowed === null;
  /** Bir modül bayi tarafından açılabilir mi. */
  const isAllowedForDealer = (id: ModuleId) => noDealerLimit || dealerAllowed!.includes(id);
  const allowedCount = dealerAllowed?.length ?? ALL_MODULE_IDS.length;

  // Demo durumu (görsel amaçlı yerel hesap; asıl kilit kararı sunucudadır)
  const demoDate = demoUntil ? new Date(demoUntil) : null;
  const demoIsExpired = demoDate !== null && demoDate.getTime() <= Date.now();
  const demoDaysLeft = demoDate
    ? Math.ceil((demoDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  /** Hızlı uzatma: mevcut tarihten (geçmişse bugünden) +gün. */
  const handleExtendDemo = (days: number) => {
    setDirty(true);
    setSuccess(null);
    const base = demoDate && demoDate.getTime() > Date.now() ? demoDate : new Date();
    const next = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    setDemoUntil(next.toISOString());
  };

  const handleDemoDateChange = (value: string) => {
    setDirty(true);
    setSuccess(null);
    setDemoUntil(dateInputToIso(value));
  };

  const handleRemoveDemo = () => {
    setDirty(true);
    setSuccess(null);
    setDemoUntil(null);
  };

  const handleAllEnabledToggle = (checked: boolean) => {
    setDirty(true);
    setSuccess(null);
    // Kısıtlamaya geçerken mevcut durumu koru: tümü açıktan geçişte hepsi seçili başlar.
    setModules(checked ? null : [...DEFAULT_ON_MODULE_IDS]);
  };

  const handleModuleToggle = (id: ModuleId, checked: boolean) => {
    setDirty(true);
    setSuccess(null);
    setModules((prev) => {
      const current = prev ?? [...DEFAULT_ON_MODULE_IDS];
      if (checked && id === 'production_ops' && !current.includes('production')) return current;
      let next = checked ? Array.from(new Set([...current, id])) : current.filter((m) => m !== id);
      if (!checked && id === 'production') next = next.filter((m) => m !== 'production_ops');
      return next;
    });
  };

  /** Merkez: bayi kısıtlamasını tamamen kaldırır / geri getirir. */
  const handleNoDealerLimitToggle = (checked: boolean) => {
    setAllowedDirty(true);
    setSuccess(null);
    // Kısıtlamaya geçerken şirketin hâlihazırda açık modülleri tavan olarak başlar —
    // en az kesintili varsayılan budur.
    setDealerAllowed(checked ? null : modules ?? [...DEFAULT_ON_MODULE_IDS]);
  };

  /**
   * Merkez: tavanı değiştirir. Tavandan çıkarılan modül açık modüllerden de düşer
   * (sunucu da aynısını yapar; yerelde de yapıyoruz ki ekran ve kayıt tutarlı kalsın).
   */
  const handleAllowedToggle = (id: ModuleId, checked: boolean) => {
    setAllowedDirty(true);
    setSuccess(null);
    const ceiling = dealerAllowed ?? [...DEFAULT_ON_MODULE_IDS];
    if (checked && id === 'production_ops' && !ceiling.includes('production')) return;

    const nextAllowed = checked
      ? Array.from(new Set([...ceiling, id]))
      : ceiling.filter((m) => m !== id && !(id === 'production' && m === 'production_ops'));
    setDealerAllowed(nextAllowed);

    // İzin geri alınırken modül şirkette açıksa yerelde de kapatılır.
    const currentModules = modules ?? [...DEFAULT_ON_MODULE_IDS];
    if (!checked && (currentModules.includes(id) || (id === 'production' && currentModules.includes('production_ops')))) {
      setModules(currentModules.filter((m) => m !== id && !(id === 'production' && m === 'production_ops')));
      setDirty(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const service = SuperAdminService.getInstance();
      // Tavan ÖNCE kaydedilir: sunucu tavana göre açık modülleri kırpar, ardından
      // gönderdiğimiz açık modül listesi (yerelde de kırpılmış) çakışmadan geçer.
      if (allowedDirty) {
        await service.setDealerAllowedModules(company.id, dealerAllowed);
      }
      if (dirty) {
        await service.setCompanyModules(company.id, modules, demoUntil);
      }
      setSuccess(
        scopeIsDealer
          ? 'Modül ayarları kaydedildi.'
          : 'Modül, bayi izni ve demo ayarları kaydedildi.'
      );
      setDirty(false);
      setAllowedDirty(false);
    } catch (e: any) {
      setError(e?.message || 'Modül ayarları kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = modules?.length ?? DEFAULT_ON_MODULE_IDS.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <ExtensionIcon sx={{ color: '#0288d1' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" noWrap>
            Modül Yönetimi
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {company.name}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={
            scopeIsDealer
              ? `İzinli ${allowedCount} modülden ${enabledCount} açık`
              : allEnabled
                ? 'Tümü açık'
                : `${enabledCount}/${ALL_MODULE_IDS.length} modül açık`
          }
          color={!scopeIsDealer && allEnabled ? 'success' : 'primary'}
          variant="outlined"
        />
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* ── Bayi modu bilgilendirmesi ─────────────────────────────── */}
            {scopeIsDealer && (
              <Alert
                severity={dealerAllowed !== null && allowedCount === 0 ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                {dealerAllowed !== null && allowedCount === 0
                  ? 'Merkez bu şirket için henüz modül izni vermedi. Satmak istediğiniz modüller için merkez ile iletişime geçin.'
                  : 'Yalnızca merkezin izin verdiği modülleri açabilirsiniz. Kilitli modüller için merkez ile iletişime geçin.'}
              </Alert>
            )}

            {/* "Tümü açık" yetkisi merkeze özel — bayi tavanı atlayamaz. */}
            {!scopeIsDealer && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allEnabled}
                      onChange={(e) => handleAllEnabledToggle(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Tüm modüller açık (varsayılan)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Kapatırsan aşağıdan şirketin satın aldığı modülleri seçebilirsin.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />

                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            <Box>
              {MODULE_DEFINITIONS.map((def) => {
                const isOptIn = OPT_IN_MODULE_IDS.includes(def.id);
                // "Tümü açık" varsayılan satırları dondurur. Opt-in satır işaretsiz ve tıklanabilir kalır.
                const frozen = !scopeIsDealer && allEnabled && !isOptIn;
                const productionSelected = modules?.includes('production') ?? false;
                const opsBlocked = def.id === 'production_ops' && !productionSelected;
                const checked = frozen || (modules?.includes(def.id) ?? false);
                // Bayi modunda tavan dışı modül kilitli; merkez her modülü açabilir.
                const locked = scopeIsDealer && !isAllowedForDealer(def.id);
                const disabled = locked || frozen || opsBlocked;
                return (
                  <Box
                    key={def.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      opacity: locked || opsBlocked ? 0.5 : frozen ? 0.45 : 1,
                      pointerEvents: frozen ? 'none' : 'auto',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ minWidth: 0, mr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {def.labelTr}
                        </Typography>
                        {isOptIn && (
                          <Chip size="small" label="Ayrıca açılır" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        )}
                        {locked && <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {opsBlocked ? 'Önce Üretim modülünü açın' : locked ? 'Merkez izni yok' : def.descriptionTr}
                      </Typography>
                    </Box>
                    <Tooltip
                      title={opsBlocked ? 'Önce Üretim modülünü açın' : locked ? 'Bu modül için merkez izni yok' : ''}
                      disableHoverListener={!locked && !opsBlocked}
                    >
                      <Switch
                        size="small"
                        checked={checked && !opsBlocked}
                        disabled={disabled}
                        onChange={(e) => handleModuleToggle(def.id, e.target.checked)}
                      />
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>

            {!allEnabled && enabledCount === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Hiçbir modül seçili değil — şirket kullanıcıları girişte hiçbir menü göremez.
              </Alert>
            )}

            {/* ── Bayi Modül İzinleri (TAVAN) — yalnız merkez, yalnız bayi şirketi ── */}
            {!scopeIsDealer && isDealerManaged && (
              <>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StorefrontIcon fontSize="small" sx={{ color: '#7b1fa2' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Bayiye İzin Verilen Modüller
                  </Typography>
                  <Chip
                    size="small"
                    label={noDealerLimit ? 'Kısıt yok' : `${allowedCount}/${ALL_MODULE_IDS.length} izinli`}
                    color={noDealerLimit ? 'default' : 'secondary'}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Bayi yalnızca burada izin verdiğiniz modülleri kendi portalından açabilir.
                  İzni geri aldığınız modül, şirkette açıksa otomatik kapanır.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={noDealerLimit}
                      onChange={(e) => handleNoDealerLimitToggle(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Bayi kısıtlaması yok
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Açıkken bayi bu şirkette her modülü kendi açabilir.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />

                <Box
                  sx={{
                    opacity: noDealerLimit ? 0.45 : 1,
                    pointerEvents: noDealerLimit ? 'none' : 'auto',
                  }}
                >
                  {MODULE_DEFINITIONS.map((def) => {
                    const productionAllowed = dealerAllowed?.includes('production') ?? false;
                    const opsBlocked = def.id === 'production_ops' && !noDealerLimit && !productionAllowed;
                    const allowed = !opsBlocked && (noDealerLimit || (dealerAllowed?.includes(def.id) ?? false));
                    const activeByDealer = modules?.includes(def.id) ?? false;
                    return (
                      <Box
                        key={`allowed-${def.id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          opacity: opsBlocked ? 0.5 : 1,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mr: 1 }}>
                          <Typography variant="body2">{def.labelTr}</Typography>
                          {opsBlocked && (
                            <Typography variant="caption" color="text.secondary">Önce Üretim modülünü açın</Typography>
                          )}
                          {allowed && (
                            <Chip
                              size="small"
                              label={activeByDealer ? 'Bayi açtı' : 'Bayi açmadı'}
                              color={activeByDealer ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                        </Box>
                        <Switch
                          size="small"
                          color="secondary"
                          checked={allowed}
                          disabled={opsBlocked}
                          onChange={(e) => handleAllowedToggle(def.id, e.target.checked)}
                        />
                      </Box>
                    );
                  })}
                </Box>

                {!noDealerLimit && allowedCount === 0 && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Bayiye hiçbir modül izni verilmedi — bayi bu şirkette hiçbir modül açamaz.
                  </Alert>
                )}
              </>
            )}

            {/* ── Demo Kullanım Sınırı — yalnız merkez ─────────────────── */}
            {!scopeIsDealer && (
              <>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HourglassBottomIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Demo Kullanım Sınırı
                  </Typography>
                  {demoUntil ? (
                    <Chip
                      size="small"
                      label={
                        demoIsExpired
                          ? 'Demo doldu'
                          : `Demo — ${Math.max(0, demoDaysLeft ?? 0)} gün kaldı`
                      }
                      color={demoIsExpired ? 'error' : 'warning'}
                    />
                  ) : (
                    <Chip size="small" label="Demo değil (süresiz)" color="success" variant="outlined" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Tarih geçtiğinde tüm modüller otomatik kapanır ve kullanıcılar kilit ekranı görür.
                  Boş bırakmak demoyu kaldırır (kalıcı müşteri).
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <AppDatePicker
                    size="small"
                    label="Demo bitiş tarihi"
                    value={toDateInputValue(demoUntil)}
                    onChange={handleDemoDateChange}
                    sx={{ width: 190 }}
                  />
                  <Button size="small" variant="outlined" onClick={() => handleExtendDemo(7)}>
                    +7 gün
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleExtendDemo(14)}>
                    +14 gün
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleExtendDemo(30)}>
                    +30 gün
                  </Button>
                  {demoUntil && (
                    <Button size="small" color="error" onClick={handleRemoveDemo}>
                      Demoyu kaldır
                    </Button>
                  )}
                </Box>

                {demoIsExpired && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Demo süresi dolmuş — şirket kullanıcıları şu anda kilit ekranı görüyor.
                    Tarihi uzatın veya demoyu kaldırın.
                  </Alert>
                )}
              </>
            )}

            {/* Bayi modunda demo bilgisi salt-okunur gösterilir (uzatma yetkisi merkezde). */}
            {scopeIsDealer && demoUntil && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert severity={demoIsExpired ? 'error' : 'warning'}>
                  {demoIsExpired
                    ? 'Şirketin demo süresi dolmuş — kullanıcılar kilit ekranı görüyor. Uzatma için merkez ile iletişime geçin.'
                    : `Şirket demo kullanımında — ${Math.max(0, demoDaysLeft ?? 0)} gün kaldı. Süre uzatma yetkisi merkezdedir.`}
                </Alert>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Kapat
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || (!dirty && !allowedDirty)}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleSettingsDialog;
